<script lang="ts">
import { defineComponent, ref, nextTick, onMounted } from 'vue';
import { ExampleConfig } from '../types';
import ConfigForm from './ConfigForm.vue';
import ConfigFormFields from './ConfigFormFields.vue';
import WebIde from './WebIde.vue';
import { saveConfig, loadConfig } from '../configStorage';
import { getConfigFromUrl } from '../utils/url-parser';
import { fetchGitLabConfig } from '../utils/api';

export default defineComponent({
  props: {
    autostart: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    ConfigForm,
    ConfigFormFields,
    WebIde,
  },
  setup(props) {
    const loading = ref(true);
    const error = ref(false);
    const config = ref<ExampleConfig | undefined>(undefined);
    const runWebIde = ref(false);

    // Initial config load
    onMounted(async () => {
      try {
        const savedConfig = loadConfig();
        const urlConfig = getConfigFromUrl();
        
        // Fetch config from API
        const apiConfig = await fetchGitLabConfig();
        
        if (!apiConfig) {
          error.value = true;
          loading.value = false;
          // Remove loader if error
          document.getElementById('initial-loader')?.remove();
          return;
        }

        // Merge configurations
        // Priority: API > URL > Saved
        const mergedConfig: ExampleConfig = {
          ...savedConfig?.config,
          ...urlConfig,
          gitlabUrl: apiConfig.gitlab_url,
          gitlabToken: apiConfig.gitlab_pat,
          authType: 'token', 
        } as ExampleConfig;

        config.value = mergedConfig;

        // Check if we have enough config to auto-start
        const canAutoStart = mergedConfig.gitlabUrl && mergedConfig.projectPath && mergedConfig.gitRef;
        
        if (canAutoStart || props.autostart) {
          runWebIde.value = true;
        }
      } catch (e) {
        console.error('Error initializing App:', e);
        error.value = true;
      } finally {
        loading.value = false;
        if (!runWebIde.value) {
          document.getElementById('initial-loader')?.remove();
        }
      }
    });

    // callbacks
    const start = () => {
      runWebIde.value = true;
    };

    const onSubmit = (configArg: ExampleConfig) => {
      saveConfig({
        config: configArg,
      });

      config.value = configArg;

      start();
    };

    return {
      runWebIde,
      onSubmit,
      config,
      loading,
      error,
    };
  },
});
</script>

<template>
  <div v-if="loading" class="loading-state">
    <!-- Optional: Add a spinner or customized loading UI here if the initial-loader is removed too early or not present -->
  </div>
  <div v-else-if="error">
      <config-form-fields />
  </div>
  <template v-else>
    <web-ide v-if="runWebIde && config" :config="config" />
    <div v-else>
      <config-form :init-config="config" @submit="onSubmit" />
    </div>
  </template>
</template>
